import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Search, X } from "lucide-react";
import { listSuppliers } from "../../api/purchases";
import { SupplierEmpty, SupplierOption, supplierLabel } from "./SupplierOption";

// اختيار المورّد: بطاقة بارزة عند الاختيار + بحث حي عبر الخادم.
export const SupplierPicker = ({ value, onChange, allowEmpty = false, testid = "supplier-picker" }) => {
  const { t, i18n } = useTranslation();
  const ar = i18n.language === "ar";
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [picked, setPicked] = useState(null);
  const [open, setOpen] = useState(false);
  const box = useRef(null);

  useEffect(() => {
    const id = setTimeout(() => {
      listSuppliers({ search: query || undefined, limit: 20 })
        .then((data) => setItems(data.items || []))
        .catch(() => setItems([]));
    }, 250);
    return () => clearTimeout(id);
  }, [query]);

  useEffect(() => {
    const away = (event) => {
      if (box.current && !box.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, []);

  useEffect(() => { if (!value) setPicked(null); }, [value]);

  const choose = (supplier) => {
    setPicked(supplier);
    setQuery("");
    setOpen(false);
    onChange(supplier ? supplier.id : "");
  };

  return (
    <div className="relative" ref={box} data-testid={testid}>
      {picked && !open ? (
        <div data-testid={`${testid}-picked`}
             onClick={() => setOpen(true)}
             className="flex w-full cursor-pointer items-center gap-3 rounded-2xl bg-blue-600 px-4 py-3 shadow-neu-in ring-2 ring-blue-400 dark:bg-blue-500">
          <span className="min-w-0 flex-1 truncate text-sm font-extrabold text-white">
            {supplierLabel(picked, ar)}
          </span>
          {picked.tax?.trn ? (
            <span className="num shrink-0 font-mono text-[10px] text-blue-50/90">
              {picked.tax.trn}
            </span>
          ) : null}
          <button type="button" data-testid={`${testid}-clear`}
                  onClick={(e) => { e.stopPropagation(); choose(null); }}
                  className="rounded-full bg-white/20 p-1 hover:bg-white/40">
            <X className="h-4 w-4 text-white" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2 rounded-2xl bg-background px-3 py-2 shadow-neu-in">
          <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <input data-testid={`${testid}-input`} autoFocus={open}
                 className="min-w-0 flex-1 bg-transparent text-sm font-bold outline-none"
                 placeholder={t("purchases.searchPickSupplier")}
                 value={query}
                 onFocus={() => setOpen(true)}
                 onChange={(e) => { setQuery(e.target.value); setOpen(true); }} />
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </div>
      )}

      {open ? (
        <div className="absolute z-20 mt-2 max-h-64 w-full overflow-auto rounded-2xl bg-background p-1 shadow-neu"
             data-testid={`${testid}-list`}>
          {allowEmpty ? (
            <button type="button" data-testid={`${testid}-none`} onClick={() => choose(null)}
                    className="w-full rounded-xl px-3 py-2.5 text-start text-xs font-bold text-muted-foreground transition-colors hover:bg-blue-500 hover:text-white focus:bg-blue-500 focus:text-white focus:outline-none">
              {t("purchases.noSupplier")}
            </button>
          ) : null}
          {!items.length ? <SupplierEmpty testid={`${testid}-empty`} /> : items.map((supplier) => (
            <SupplierOption key={supplier.id} supplier={supplier} ar={ar} onPick={choose}
                            active={picked?.id === supplier.id}
                            testid={`${testid}-option-${supplier.id}`} />
          ))}
        </div>
      ) : null}
    </div>
  );
};
